import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { store } from '../store';
import { updateProfessionalInfo } from '../redux/slices/userProfileSlice';
import { Admin_SaveMatches } from '../constants/adminData';
import { getSensitiveData } from '../utils/secureStorage';

export interface SaveToast {
  message: string;
  type: 'success' | 'error' | 'warning';
}

export const useSaveDesiredPartnerProfile = () => {
  const dispatch = useDispatch();
  const [toast, setToast] = useState<SaveToast | null>(null);

  const clearToast = () => setToast(null);

  const mapPayload = async (state: any, preferredLocation?: string) => {
    const userId = state.userId || (await getSensitiveData('userId'));
    const location = preferredLocation ?? state.professionalInfo?.preferredLocation ?? '';

    return {
      userId,

      preferredLocation: location,

      itspecialityIds: (state.professionalInfo?.preferredspeciality || []).join(','),

      skillsIds: (state.professionalInfo?.preferredSkills || [])
        .map((s: any) => s.skillId)
        .join(','),

      workTypeId: state.professionalInfo?.preferredWorkType ?? 0,

      minExperience: state.personalInfo?.desiredExperinceMin ?? 0,
      maxExperience: state.personalInfo?.desiredExperienceMax ?? 0,

      desiredAgeMin: state.personalInfo?.desiredAgeMin ?? 18,
      desiredAgeMax: state.personalInfo?.desiredAgeMax ?? 18,

      matchWithSimilarSpecialists: state.professionalInfo?.desiredSpeciality ?? false,

      desiredWorkShiftId: state.professionalInfo?.workShiftDesiredID ?? 0,

      desiredGenderId: state.personalInfo?.desiredGenderId ?? 0,

      attractiveAppearanceImportance: state.personalInfo?.attactiveAppearanceImportance
        ? Number(state.personalInfo.attactiveAppearanceImportance)
        : 2,

      desiredHeightMinInches: state.personalInfo?.desiredHeightInches ?? 0,
      desiredHeightMaxInches: state.personalInfo?.desiredHeightInches ?? 0,

      desiredWeightMinLbs: state.personalInfo?.desiredWeight ?? 0,
      desiredWeightMaxLbs: state.personalInfo?.desiredWeight ?? 0,

      desiredReligionId: state.backgroundInfo?.desiredReligionId ?? 0,

      desiredLocationRangeMiles: state.personalInfo?.desiredLocation ?? 0,

      desiredEthnicities: state.backgroundInfo?.desiredEthnicities ?? '',

      desiredHeightInches: state.personalInfo?.desiredHeightInches ?? 0,
      desiredHeightFeet: state.personalInfo?.desiredHeightFeet ?? 0,
      desiredHeightMeters: state.personalInfo?.desiredHeightMeters ?? 0,
      desiredHeightCentimeters: state.personalInfo?.desiredHeightCentimeters ?? 0,
      desiredHeightUnitSystem: state.personalInfo?.desiredHeightUnitSystem ?? 'Metric',

      desiredWeight: state.personalInfo?.desiredWeight ?? 0,
    };
  };

  const saveDesiredPartnerProfile = async (preferredLocation?: string) => {
    // Always read the LATEST state from the store at call-time
    const latestProfile = store.getState().userProfile;

    const profileToSave = preferredLocation === undefined
      ? latestProfile
      : {
          ...latestProfile,
          professionalInfo: { ...latestProfile.professionalInfo, preferredLocation },
        };

    const payload = await mapPayload(profileToSave, preferredLocation);

    const response = await Admin_SaveMatches(payload);

    if (!response?.success) {
      const message = response?.message || 'Failed to save preferences';
      setToast({ message, type: 'error' });
      throw new Error(message);
    }

    if (preferredLocation !== undefined) {
      dispatch(updateProfessionalInfo({ preferredLocation }));
    }
    setToast({ message: response?.message ?? 'Preferences saved successfully!', type: 'success' });
    return response;
  };

  return { saveDesiredPartnerProfile, toast, clearToast };
};
